import { NgTemplateOutlet } from '@angular/common';
import { Component } from '@angular/core';

import { FnCallPipe } from '@recruitee/common';
import { TranslatePipe } from '@recruitee/i18n';
import { JobBoardCodename } from '@recruitee/job-boards-types';
import { AssignedLocationsInfoContainerComponent } from '@recruitee/locations';
import { OfferStatus } from '@recruitee/offers-types';
import {
  BoxComponent,
  ButtonComponent,
  DialogBodyComponent,
  DialogComponentI,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  DividerComponent,
  FixedSelectOptionsHelper,
  HeadingComponent,
  IconComponent,
  RtTooltip,
  SelectOption,
} from '@recruitee/user-interface';

type WorkModel = {
  onSite: boolean;
  remote: boolean;
  hybrid: boolean;
};

type JobPublishedDialogData = {
  status: OfferStatus.Internal | OfferStatus.Published;
  title: string;
  department: string | null;
  locationIds: number[];
  workModel: WorkModel;
  priority: string | null;
  autoPostings: Array<JobBoardCodename.GoogleForJobs | JobBoardCodename.Indeed>;
  isJobEditorContext: boolean;
};

@Component({
  selector: 'rt-job-published-dialog',
  standalone: true,
  imports: [
    DialogHeaderComponent,
    DialogBodyComponent,
    BoxComponent,
    DialogFooterComponent,
    ButtonComponent,
    TranslatePipe,
    IconComponent,
    DividerComponent,
    HeadingComponent,
    NgTemplateOutlet,
    AssignedLocationsInfoContainerComponent,
    FnCallPipe,
    RtTooltip,
  ],
  templateUrl: './job-published-dialog.component.html',
  styleUrl: './job-published-dialog.component.less',
})
export class JobPublishedDialogComponent implements DialogComponentI<JobPublishedDialogData, void> {
  public readonly translations = {
    [OfferStatus.Internal]: {
      heading: 'app.offers.job_published_dialog.internal.heading',
    },
    [OfferStatus.Published]: {
      heading: 'app.offers.job_published_dialog.published.heading',
    },
    publishedOn: 'app.offers.job_published_dialog.published_on',
    internalUse: 'app.offers.job_published_dialog.internal_use',
    careersSite: 'app.offers.job_published_dialog.careers_site',
    googleForJobs: 'app.offers.job_published_dialog.google_for_jobs',
    indeed: 'app.offers.job_published_dialog.indeed',
    keepEditing: 'app.offers.job_published_dialog.keep_editing',
    letsGo: 'app.offers.job_published_dialog.lets_go',
    viewJobsPipeline: 'app.offers.job_published_dialog.view_jobs_pipeline',
  };

  public status: OfferStatus.Internal | OfferStatus.Published;
  public title: string;
  public department: string | null;
  public locationIds: number[];
  public workModel: WorkModel;
  public priority: string | null;
  public autoPostings: Array<JobBoardCodename.GoogleForJobs | JobBoardCodename.Indeed>;
  public isJobEditorContext: boolean;
  public ctaButtonLabelKey: string;

  private prioritiesSelectOptions: SelectOption[];

  constructor(
    private fixedSelectOptions: FixedSelectOptionsHelper,
    private dialogRef: DialogRef,
  ) {
    this.prioritiesSelectOptions = this.fixedSelectOptions.getPrioritySelectOptions();
  }

  public get heading(): string {
    return this.translations[this.status].heading;
  }

  public get hasWorkModel(): boolean {
    return this.workModel.onSite || this.workModel.remote || this.workModel.hybrid;
  }

  public get hasElementsBeforePriority(): boolean {
    return !!this.department || !!this.locationIds.length || this.hasWorkModel;
  }

  public get priorityOption(): SelectOption | undefined {
    return this.prioritiesSelectOptions.find(option => option.value === this.priority);
  }

  public get hasAnyDetails(): boolean {
    return (
      !!this.department || !!this.locationIds.length || this.hasWorkModel || !!this.priorityOption
    );
  }

  public get isInternalJob(): boolean {
    return this.status === OfferStatus.Internal;
  }

  public get publishedOnGoogleForJobs(): boolean {
    return this.autoPostings.includes(JobBoardCodename.GoogleForJobs);
  }

  public get publishedOnIndeed(): boolean {
    return this.autoPostings.includes(JobBoardCodename.Indeed);
  }

  public isTruncatedFactory = (elRef: HTMLElement) => () => elRef.offsetWidth < elRef.scrollWidth;

  public setDialogData(data: JobPublishedDialogData, triggerUpdate: boolean): void {
    this.status = data.status;
    this.title = data.title;
    this.department = data.department;
    this.locationIds = data.locationIds;
    this.workModel = data.workModel;
    this.priority = data.priority;
    this.autoPostings = data.autoPostings;
    this.isJobEditorContext = data.isJobEditorContext;
    this.ctaButtonLabelKey = this.isInternalJob
      ? this.translations.viewJobsPipeline
      : this.translations.letsGo;
  }

  public close(): void {
    this.dialogRef.close();
  }

  public onCtaButtonClick(): void {
    this.dialogRef.close(true);
  }
}
